import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { TaskObjectState } from '../task/task-object.state';
import {
    InitializeTransaction,
    PrepareRequest,
    ResetState,
    SetConfigInfo,
    SetObjectState,
    SetPageMode
} from './interface-object.actions';

export interface InterfaceObjectStateModel {
    entities: any;
    interface: any;
    config: any;
    pageMode: boolean;
}

const InterfaceObjectStateDefaults: InterfaceObjectStateModel = {
    entities: {},
    interface: {},
    config: {},
    pageMode: false
};

@State<InterfaceObjectStateModel>({
    name: 'interfaceObjectState',
    defaults: InterfaceObjectStateDefaults
})
@Injectable()
export class InterfaceObjectState {
    constructor(
        private helper: StateHelperService,
        private app: AppService,
        private store: Store,
        public objectService: ObjectService
    ) { }

    //#region *** SELECTORS  ***

    @Selector()
    static entities(state: InterfaceObjectStateModel) {
        let tempEntities = { ...state.entities };
        tempEntities.Interface = state.interface;
        return tempEntities;
    }

    @Selector()
    static interface(state: InterfaceObjectStateModel) {
        let result: any = {};
        for (var i in state.interface) {
            result = state.interface[i];
            break;
        }
        return result;
    }

    @Selector()
    static interfaceStatus(state: InterfaceObjectStateModel) {
        let status: any = '';
        for (var i in state.interface) {
            status = state.interface[i].Status_Desc;

            break;
        }
        return status;
    }

    @Selector()
    static config(state: InterfaceObjectStateModel) {
        return state.config || {};
    }

    @Selector()
    static pageMode(state: InterfaceObjectStateModel) {
        return state.pageMode || false;
    }

    //#endregion

    //#region   *** ACTIONS  ***

    @Action(PrepareRequest)
    prepareRequest(sc: StateContext<InterfaceObjectStateModel>, action: PrepareRequest) {
        // prepare ConceptOneRequest
        // Note: The normalize process will create a Root, Reply, and Transaction entity with an ID of 0
        //       When preparing a process transaction a Root, Request, and Transaction entities will have an ID of 1
        //       (then we will denormalize the Root entity with ID = 1)

        const state = sc.getState();

        let origTran = state.entities.Transaction[0];
        let origReply = state.entities.Reply[0];
        let projectID = this.store.selectSnapshot(TaskObjectState.currentInfo).Project_ID;

        let processRoot = {
            '?xml': { '@version': '1.0' },
            ConceptOneRequest: '1'
        };

        let processTran = {
            Book_Entity_ID: origTran.Book_Entity_ID,
            Book_Loc_ID: origTran.Book_Loc_ID,
            Keep_Project_Open: 'Y',
            Session_ID: projectID || '',
            Interface: origTran['Interface'],
            ...action.tranOptions
        };

        let processRequest = {
            InterfaceProcess: '1',
            SDK_Version: origReply.SDK_Version
        };

        sc.patchState({
            entities: {
                ...state.entities,
                Root: {
                    ...state.entities.Root,
                    [1]: processRoot
                },
                Request: {
                    ...state.entities.Request,
                    [1]: processRequest
                },
                Transaction: {
                    ...state.entities.Transaction,
                    [1]: processTran
                }
            }
        });
    }

    @Action(SetObjectState)
    setObjectState(sc: StateContext<InterfaceObjectStateModel>, action: SetObjectState) {
        // Note: this is temporary to manually load state.  Later we'll have an action to load data and normalize.  
        // This does  NOT clone the passed in object.
        // A true "SetFullState" should deep clone the object. How will this trigger change for selectors at lower levels of state. Object destroy?

        const state = sc.getState();

        // copy references to raw normalized entities and then remove the entities that have their own state
        let entities = { ...action.entities };
        delete entities.Interface;

        // make sure that entities "selector" get entities from all substates.   if a new substate is added here then we need to add to the selector
        sc.patchState({
            entities: entities,
            interface: action.entities.Interface || {}
        });
    }

    @Action(ResetState)
    resetState(sc: StateContext<InterfaceObjectStateModel>, action: ResetState) {
        sc.patchState({
            entities: {},
            interface: {},
            config: {},
            pageMode: false
        });
    }

    @Action(InitializeTransaction)
    initializeTransaction(sc: StateContext<InterfaceObjectStateModel>, action: InitializeTransaction) {
        let interfaceObject = action.interfaceObject;
        const objectConfig = this.objectService.getObjectConfig(interfaceObject.Object_Code);
        let newInterface: any = {
            Object_Code: interfaceObject.Object_Code,
            Interface_GUID: '-1',
            Interface_Name: objectConfig.Object_Name,
            Interface_Desc: objectConfig.Object_Desc,
            Status: 'I',
            Status_Desc: 'In Process'
        };

        if (interfaceObject.Entity?.Entity_ID && interfaceObject.Entity?.Role_Type) {
            newInterface.Entity = {
                Entity_ID: interfaceObject.Entity.Entity_ID,
                Role_Type: interfaceObject.Entity.Role_Type
            };
        }

        if (interfaceObject.Contract?.Contract_ID) {
            newInterface.Contract = {
                Contract_ID: interfaceObject.Contract.Contract_ID,
                Lock_GUID: interfaceObject.Contract.Lock_GUID || ''
            };
        }

        sc.patchState({
            entities: {
                Root: { 0: { ConceptOneReply: '0' } },
                Reply: { 0: { InterfaceGet: '0' } },
                Transaction: {
                    0: {
                        Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                        Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
                        Interface: '-1'
                    }
                }
            },
            interface: {
                ['-1']: newInterface
            }
        });
    }

    @Action(SetConfigInfo)
    setConfigInfo(sc: StateContext<InterfaceObjectStateModel>, action: SetConfigInfo) {
        sc.patchState({
            config: action.config
        });
    }

    @Action(SetPageMode)
    setPageMode(sc: StateContext<InterfaceObjectStateModel>, action: SetPageMode) {
        sc.patchState({
            pageMode: true
        });
    }

    //#endregion
}
